import React, { useEffect, useState } from "react";

import { formatDate } from "react-lib/utils/dateUtils";

import { AlertV4, ButtonV4, FieldV4, ModalV4, SelectV4 } from "../common/ui";

import { errorTextOf } from "../common/errorTextV4";
import {
  MAKE_ACTIONS,
  MAKE_SEQUENCE,
  PATIENT_CREATE_SEARCH,
  blockFor,
  dateKeyOf,
  fullSlotText,
  hnOf,
  parseDateInput,
  personName,
  providerOptions,
  saveParamsOf,
  searchKeyOf,
  timeSlots,
  validateCreate,
  type ApiRecord,
  type Block,
  type MakeAppointmentSlice,
} from "./appointmentModelV4";
import type { AppointmentRunSequence, ItemSearchEvent } from "./CardAppointmentV4";

export type SavedAppointment = {
  id: number | string | undefined;
  name: string;
  date: Date;
  time: string;
};

type ModalCreateAppointmentV4Props = {
  make: MakeAppointmentSlice;
  blocks: Block[];
  date: Date;
  providerId: number | null;
  searchedItemListWithKey?: Record<string, unknown>;
  runSequence: AppointmentRunSequence;
  onEvent?: (event: ItemSearchEvent) => void;
  onClose: () => void;
  onSaved: (saved: SavedAppointment) => void;
};

const Required = (props: { text: string }) => (
  <>
    {props.text} <span className="hv4-appt-modal-star">*</span>
  </>
);

/** เพิ่มรายการนัดหมายใหม่ -> SAVE_APPOINTMENT ของ MakeAppointmentMeHey (ต้องมีบล็อก DSB ของวันนั้น) */
const ModalCreateAppointmentV4 = (props: ModalCreateAppointmentV4Props) => {
  const [providerId, setProviderId] = useState<number | null>(
    props.providerId ?? props.make.allProviders?.[0]?.provider_id ?? null,
  );
  const [dateText, setDateText] = useState(formatDate(props.date));
  const [manual, setManual] = useState(false);
  const [time, setTime] = useState("");
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState(false);
  const [patient, setPatient] = useState<ApiRecord | null>(null);
  const [details, setDetails] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const date = parseDateInput(dateText);
  const block = date ? blockFor(props.blocks, providerId, dateKeyOf(date), time) : null;
  const slots = block ? timeSlots(block.start, block.end) : timeSlots();

  const patientResult = props.searchedItemListWithKey?.[searchKeyOf(PATIENT_CREATE_SEARCH)] as
    ApiRecord[] | undefined;

  const shown = patient ? [patient] : searched ? (patientResult ?? []) : [];

  // Effect
  useEffect(() => {
    if (!manual && !slots.includes(time)) {
      setTime(slots[0] ?? "");
    }
  }, [manual, slots.join(","), time]);

  useEffect(() => {
    if (!saving) {
      return;
    }

    if (props.make.showSuccess && date) {
      setSaving(false);

      props.onSaved({
        id: props.make.selectedAppointmentDetail?.id,
        name: personName(patient ?? {}),
        date: date.toDate(),
        time: time.trim(),
      });
    } else if (props.make.showError) {
      setSaving(false);
      setError(errorTextOf(props.make.errorMessage) ?? "ไม่สามารถเพิ่มรายการนัดหมายได้");
    }
  }, [saving, props.make.showSuccess, props.make.showError]);

  // Handler
  const handleChangeProvider = (value: string) => {
    setProviderId(Number(value));
  };

  const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateText(e.target.value);
  };

  const handleChangeTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTime(e.target.value);
  };

  const handleChangeManual = (e: React.ChangeEvent<HTMLInputElement>) => {
    setManual(e.target.checked);
  };

  const handleChangeQuery = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPatient(null);
    setQuery(e.target.value);
  };

  const handleQueryKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleChangeDetails = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDetails(e.target.value);
  };

  const handleSearch = () => {
    setPatient(null);

    if (!query.trim()) {
      return;
    }

    props.onEvent?.({
      message: "ItemSearch",
      params: {
        ...PATIENT_CREATE_SEARCH,
        searchText: query.trim(),
        limit: 20,
        urlParams: { active: true },
      },
    });

    setSearched(true);
  };

  const handleSave = () => {
    const form = {
      providerId,
      dateText,
      manual,
      time,
      patientId: patient?.proxy_patient ?? null,
      patientName: personName(patient ?? {}),
      patientHn: patient ? hnOf(patient) : "",
    };

    const providerText =
      providerOptions(props.make.allProviders, false).find(
        (option) => option.value === String(providerId),
      )?.text ?? "";

    const message = !providerText
      ? "กรุณาเลือกผู้ให้บริการของ OA นี้"
      : validateCreate(form, props.blocks, providerText);

    setError(message);

    if (message || !block) {
      return;
    }

    if (props.make.showError) {
      props.runSequence({ sequence: MAKE_SEQUENCE, action: MAKE_ACTIONS.CLOSE_ERROR_MODAL });
    }

    setSaving(true);

    props.runSequence({
      sequence: MAKE_SEQUENCE,
      action: MAKE_ACTIONS.SAVE_APPOINTMENT,
      ...saveParamsOf({ ...form, details }, block),
    });
  };

  // Render
  return (
    <div className="hv4-appt-modal">
      <ModalV4 title="เพิ่มรายการนัดหมายใหม่" width={640} onClose={props.onClose}>
        <div className="hv4-appt-modal-body">
          {!!error && <AlertV4 tone="danger">{error}</AlertV4>}
          <FieldV4 label={<Required text="ผู้ให้บริการ" />} htmlFor="hv4-appt-new-provider">
            <SelectV4
              id="hv4-appt-new-provider"
              options={providerOptions(props.make.allProviders, false)}
              value={providerId === null ? "" : String(providerId)}
              onChange={handleChangeProvider}
            />
          </FieldV4>
          <FieldV4 label="วันที่ให้บริการ (พ.ศ. หรือ ค.ศ.)" htmlFor="hv4-appt-new-date">
            <input
              id="hv4-appt-new-date"
              className="hv4-input"
              placeholder="วว/ดด/ปปปป"
              value={dateText}
              onChange={handleChangeDate}
            />
          </FieldV4>
          <FieldV4 label="ช่วงเวลาที่ให้บริการ" htmlFor="hv4-appt-new-start">
            <div className="hv4-appt-modal-pair">
              <input
                id="hv4-appt-new-start"
                className="hv4-input"
                readOnly
                aria-label="เวลาเริ่ม"
                value={block?.start ?? "-"}
              />
              <span className="hv4-appt-modal-to">ถึง</span>
              <input
                className="hv4-input"
                readOnly
                aria-label="เวลาสิ้นสุด"
                value={block?.end ?? "-"}
              />
            </div>
          </FieldV4>
          <FieldV4 label={<Required text="เวลานัดหมาย" />} htmlFor="hv4-appt-new-time">
            <div className="hv4-appt-modal-pair">
              {manual ? (
                <input
                  id="hv4-appt-new-time"
                  className="hv4-input"
                  placeholder="เช่น 10:15"
                  value={time}
                  onChange={handleChangeTime}
                />
              ) : (
                <SelectV4
                  id="hv4-appt-new-time"
                  options={slots.map((slot) => ({
                    value: slot,
                    text: block?.full ? `${slot} (เต็ม)` : slot,
                  }))}
                  value={time}
                  onChange={setTime}
                />
              )}
              <label className="hv4-appt-check">
                <input type="checkbox" checked={manual} onChange={handleChangeManual} />
                กำหนดเวลาแบบ manual
              </label>
            </div>
          </FieldV4>
          {block?.full && !!time.trim() && (
            <AlertV4 tone="warn">{fullSlotText(time.trim(), manual)}</AlertV4>
          )}
          <FieldV4 label={<Required text="ชื่อผู้รับบริการ" />} htmlFor="hv4-appt-new-patient">
            <input
              id="hv4-appt-new-patient"
              className="hv4-input"
              placeholder="ค้นหาชื่อ - นามสกุล หรือ HN แล้วกด Enter"
              value={patient ? personName(patient) : query}
              onChange={handleChangeQuery}
              onKeyDown={handleQueryKeyDown}
            />
            {(searched || patient) && (
              <ul className="hv4-appt-picks" role="listbox" aria-label="ผลค้นหาผู้รับบริการ">
                {shown.length ? (
                  shown.map((item) => (
                    <li
                      key={item.proxy_patient}
                      role="option"
                      aria-selected={patient?.proxy_patient === item.proxy_patient}
                      className={`hv4-appt-pick${
                        patient?.proxy_patient === item.proxy_patient ? " hv4-appt-pick--on" : ""
                      }`}
                      onClick={() => setPatient(item)}
                    >
                      {[hnOf(item), personName(item)].filter(Boolean).join(" · ")}
                    </li>
                  ))
                ) : (
                  <li className="hv4-appt-picks-empty">
                    {patientResult === undefined ? "กำลังค้นหา" : "ไม่พบผู้รับบริการ"}
                  </li>
                )}
              </ul>
            )}
          </FieldV4>
          <FieldV4 label="เบอร์โทรติดต่อ" htmlFor="hv4-appt-new-tel">
            <input
              id="hv4-appt-new-tel"
              className="hv4-input"
              readOnly
              value={patient?.phone_no || "-"}
            />
          </FieldV4>
          <FieldV4 label="รายละเอียดการนัดหมาย" htmlFor="hv4-appt-new-detail">
            <textarea
              id="hv4-appt-new-detail"
              className="hv4-input hv4-textarea"
              rows={3}
              placeholder="ระบุรายละเอียดนัดหมาย"
              value={details}
              onChange={handleChangeDetails}
            />
          </FieldV4>
        </div>
        <div className="hv4-appt-modal-foot">
          <ButtonV4 className="hv4-appt-modal-close" onClick={props.onClose}>
            ปิดหน้าจอ
          </ButtonV4>
          <ButtonV4 className="hv4-appt-modal-save" disabled={saving} onClick={handleSave}>
            บันทึกข้อมูล
          </ButtonV4>
        </div>
      </ModalV4>
    </div>
  );
};

ModalCreateAppointmentV4.displayName = "ModalCreateAppointmentV4";

export default ModalCreateAppointmentV4;
